import { useRef, useState, type MouseEvent, type RefObject } from "react";

import { useTaskDialogStore } from "../../stores/index.js";
import { isCalendarBackgroundCreateSuppressed } from "./calendar-background-create-guard.js";

const SNAP_MIN = 15;
const DAY_MIN = 24 * 60;

interface DragRange { startDay: number; startMin: number; endDay: number; endMin: number }

/** Per-column highlight segment derived from the active drag. */
export interface CreatePreview { startMin: number; endMin: number; showLabel: boolean }

interface Opts {
  /** The timed grid container (hour-axis column + day columns). */
  gridRef: RefObject<HTMLDivElement | null>;
  /** Width of the leading hour-axis column in px. */
  axisWidthPx: number;
  dayCount: number;
  hourHeight: number;
  /** total=0 means a plain click (no duration). */
  onCreate: (startDayIdx: number, startMin: number, totalMinutes: number) => void;
}

/**
 * Drag-to-create for the week/day grid, lifted above the individual day columns
 * so a selection can span multiple days (e.g. 09:00 Mon → 10:00 Tue). Column is
 * derived from clientX, minute-of-day from clientY, both against the grid rect.
 */
export function useCreateDrag({ gridRef, axisWidthPx, dayCount, hourHeight, onCreate }: Opts) {
  const [drag, setDrag] = useState<DragRange | null>(null);
  const dragRef = useRef<DragRange | null>(null);
  // A finished drag's selection, kept on screen while the create dialog is open
  // (the caller clears it when the dialog closes) so the new entry's spot stays
  // visible while it's being filled in.
  const [pending, setPending] = useState<DragRange | null>(null);

  function set(next: DragRange | null) { dragRef.current = next; setDrag(next); }

  function colFromX(clientX: number): number {
    const el = gridRef.current;
    if (!el) return -1;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left - axisWidthPx;
    if (x < 0) return -1;
    const dayW = (rect.width - axisWidthPx) / dayCount;
    return Math.max(0, Math.min(dayCount - 1, Math.floor(x / dayW)));
  }

  function minFromY(clientY: number): number {
    const el = gridRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const raw = ((clientY - rect.top) / hourHeight) * 60;
    return Math.max(0, Math.min(DAY_MIN - SNAP_MIN, Math.floor(raw / SNAP_MIN) * SNAP_MIN));
  }

  function startCreate(e: MouseEvent) {
    if (e.button !== 0) return;
    if (isCalendarBackgroundCreateSuppressed() || useTaskDialogStore.getState().open) return;
    // Bail on existing blocks AND the create-from-calendar draft ghost — dragging
    // a ghost handle also fires mousedown (alongside the ghost's pointer events),
    // which would otherwise spawn a phantom create-drag preview next to it.
    if ((e.target as HTMLElement).closest("[data-task-block], [data-quick-ghost]")) return;
    const startDay = colFromX(e.clientX);
    if (startDay < 0) return;
    const startMin = minFromY(e.clientY);
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    setPending(null);
    set({ startDay, startMin, endDay: startDay, endMin: startMin });

    function onMove(ev: globalThis.MouseEvent) {
      const endDay = colFromX(ev.clientX);
      if (endDay < 0) return;
      set({ startDay, startMin, endDay, endMin: minFromY(ev.clientY) });
    }
    function onUp(ev: globalThis.MouseEvent) {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current;
      set(null);
      if (!d) return;
      const loAbs = Math.min(d.startDay * DAY_MIN + d.startMin, d.endDay * DAY_MIN + d.endMin);
      const hiAbs = Math.max(d.startDay * DAY_MIN + d.startMin, d.endDay * DAY_MIN + d.endMin);
      const movedFar = Math.abs(ev.clientX - startX) >= 4 || Math.abs(ev.clientY - startY) >= 4;
      if (!movedFar || hiAbs - loAbs < SNAP_MIN) {
        onCreate(d.startDay, d.startMin, 0);
      } else {
        // Pin the selection in place while the create dialog is open.
        setPending(d);
        onCreate(Math.floor(loAbs / DAY_MIN), loAbs % DAY_MIN, hiAbs - loAbs);
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function previewFor(col: number): CreatePreview | null {
    const d = drag ?? pending;
    if (!d) return null;
    const aAbs = d.startDay * DAY_MIN + d.startMin;
    const bAbs = d.endDay * DAY_MIN + d.endMin;
    const loAbs = Math.min(aAbs, bAbs), hiAbs = Math.max(aAbs, bAbs);
    if (hiAbs - loAbs < SNAP_MIN) return null;
    const loDay = Math.floor(loAbs / DAY_MIN), hiDay = Math.floor(hiAbs / DAY_MIN);
    if (col < loDay || col > hiDay) return null;
    return {
      startMin: col === loDay ? loAbs % DAY_MIN : 0,
      endMin: col === hiDay ? hiAbs % DAY_MIN : DAY_MIN,
      showLabel: loDay === hiDay,
    };
  }

  return { startCreate, previewFor, clearPending: () => setPending(null) };
}
